import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs from 'dayjs'
import 'dayjs/locale/en'
import 'dayjs/locale/es'
import 'dayjs/locale/pt'
import { useContext, useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import AppContext from '../../../context/appContext'
import { useFormDefinitions } from '../../../hooks/data/useFormDefinitions'
import { useProgramMonitoringPeriods } from '../../../hooks/data/useProgramMonitoringPeriods'
import { getDayJsLang } from '../../../utils/getDayJsLang'
import { compareByNumericName } from './attestationFormSort'

const defaultDisclaimer =
  // eslint-disable-next-line max-len
  'Todos los criterios de elegibilidad y los requisitos para la inscripción de tierras en el Programa SARA están sujetos a las normativas vigentes, a las auditorías y exigencias establecidas por la certificadora Verra.\nLa superficie elegible para el Programa comprende pastizales y/o praderas ganaderas bajo manejo holístico, descontando las áreas de exclusión, las superficies no elegibles y aquellas destinadas a cultivos anuales u otras producciones, según corresponda. Asimismo, dicha superficie deberá contar con la documentación legal que acredite la tenencia de la tierra y cumplir con las normativas y regulaciones nacionales vigentes en el país donde se encuentre.'

const AttestationReportForm = ({ onSubmit, onCancel }) => {
  const { currentFarm, setLoadingModalConfig, programConfig } = useContext(AppContext)
  const { data: formDefinitions, isLoading } = useFormDefinitions(programConfig)

  useEffect(() => {
    setLoadingModalConfig({
      open: isLoading,
      dialogMessage: 'Cargando información...',
    })
    return () => setLoadingModalConfig({ open: false })
  }, [isLoading, setLoadingModalConfig])

  const { programMonitoringPeriods } = useProgramMonitoringPeriods({
    programId: currentFarm?.programId,
  })

  const namespaces = useMemo(
    () =>
      formDefinitions?.map(({ label, namespace }) => ({
        label,
        namespace,
      })) || [],
    [formDefinitions],
  )

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      date: dayjs().startOf('day'),
      attestationContent: '',
      city: currentFarm?.city || '',
      province: currentFarm?.province || '',
      country: currentFarm?.country || '',
      responsible: '',
      disclaimer: defaultDisclaimer,
      periodStart: null,
      periodEnd: null,
      programMonitoringPeriodId: '',
      includedMetrics: {},
    },
  })
  const includedMetrics = watch('includedMetrics')
  const periodStart = watch('periodStart')
  const periodEnd = watch('periodEnd')

  const filteredPeriods = useMemo(
    () =>
      programMonitoringPeriods
        ?.filter(period => {
          if (!periodStart || !periodEnd) return false
          const startYear = dayjs(periodStart).year()
          const endYear = dayjs(periodEnd).year()
          return (
            dayjs(period.startDate).year() >= startYear && dayjs(period.endDate).year() <= endYear
          )
        })
        .sort(compareByNumericName) || [],
    [programMonitoringPeriods, periodStart, periodEnd],
  )

  const allSelected = useMemo(() => {
    if (!includedMetrics || namespaces.length === 0) return false
    return namespaces.every(({ namespace }) => includedMetrics[namespace])
  }, [includedMetrics, namespaces])

  const handleSelectAll = () => {
    const newValue = !allSelected
    namespaces.forEach(({ namespace }) => {
      setValue(`includedMetrics.${namespace}`, newValue)
    })
  }

  const handleFormSubmit = data => {
    if (!onSubmit) return
    const selectedMetrics = Object.entries(data.includedMetrics || {})
      .filter(([, value]) => value)
      .map(([key]) => key)
    const includedNamespaces = namespaces.filter(namespace =>
      selectedMetrics.includes(namespace.namespace),
    )
    onSubmit({ ...data, includedMetrics: includedNamespaces })
  }

  if (!formDefinitions) return null

  return (
    <LocalizationProvider adapterLocale={getDayJsLang()} dateAdapter={AdapterDayjs}>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Controller
              control={control}
              name="date"
              render={({ field }) => (
                <DatePicker
                  {...field}
                  label="Fecha"
                  slotProps={{
                    textField: {
                      error: !!errors.date,
                      helperText: errors.date?.message,
                    },
                  }}
                />
              )}
              rules={{ required: 'Campo requerido' }}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              control={control}
              name="attestationContent"
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  error={!!errors.attestationContent}
                  helperText={errors.attestationContent?.message}
                  label="Contenido"
                  rows={8}
                />
              )}
            />
          </Grid>
          <Grid item sm={4} xs={12}>
            <Controller
              control={control}
              name="city"
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  error={!!errors.city}
                  helperText={errors.city?.message}
                  label="Ciudad"
                />
              )}
              rules={{ required: 'Campo requerido' }}
            />
          </Grid>
          <Grid item sm={4} xs={12}>
            <Controller
              control={control}
              name="province"
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  error={!!errors.province}
                  helperText={errors.province?.message}
                  label="Provincia"
                />
              )}
              rules={{ required: 'Campo requerido' }}
            />
          </Grid>
          <Grid item sm={4} xs={12}>
            <Controller
              control={control}
              name="country"
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  error={!!errors.country}
                  helperText={errors.country?.message}
                  label="País"
                />
              )}
              rules={{ required: 'Campo requerido' }}
            />
          </Grid>
          <Grid item sm={4} xs={6}>
            <Controller
              control={control}
              name="periodStart"
              render={({ field }) => (
                <DatePicker
                  {...field}
                  label="Inicio del Periodo"
                  slotProps={{
                    textField: {
                      error: !!errors.periodStart,
                      helperText: errors.periodStart?.message,
                    },
                  }}
                  views={['year']}
                />
              )}
              rules={{ required: 'Campo requerido' }}
            />
          </Grid>
          <Grid item sm={4} xs={6}>
            <Controller
              control={control}
              name="periodEnd"
              render={({ field }) => (
                <DatePicker
                  {...field}
                  label="Fin del Periodo"
                  slotProps={{
                    textField: {
                      error: !!errors.periodEnd,
                      helperText: errors.periodEnd?.message,
                    },
                  }}
                  views={['year']}
                />
              )}
              rules={{
                required: 'Campo requerido',
                validate: {
                  isValid: value => dayjs(value).isValid() || 'Fecha inválida',
                  isAfterStart: value =>
                    !periodStart ||
                    !value ||
                    dayjs(value).isSame(dayjs(periodStart)) ||
                    dayjs(value).isAfter(dayjs(periodStart)) ||
                    'La fecha de fin debe ser igual o posterior a la fecha de inicio',
                },
              }}
            />
          </Grid>
          <Grid item sm={4} xs={12}>
            <Controller
              control={control}
              name="programMonitoringPeriodId"
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  select
                  disabled={!periodStart || !periodEnd}
                  error={!!errors.programMonitoringPeriodId}
                  helperText={errors.programMonitoringPeriodId?.message}
                  label="MR para instancias de carbono (opcional)"
                >
                  <MenuItem key="none" value="">
                    <em>Ninguno</em>
                  </MenuItem>
                  {filteredPeriods.map(period => (
                    <MenuItem key={period.id} value={period.id}>
                      {period.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              control={control}
              name="disclaimer"
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  error={!!errors.disclaimer}
                  helperText={errors.disclaimer?.message}
                  label="Texto de aviso legal (opcional)"
                  rows={4}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              control={control}
              name="responsible"
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  error={!!errors.responsible}
                  helperText={errors.responsible?.message}
                  label="Responsable"
                />
              )}
              rules={{ required: 'Campo requerido' }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1">Incluir Métricas</Typography>
            <Button onClick={handleSelectAll}>
              {allSelected ? 'Quitar Todo' : 'Seleccionar Todo'}
            </Button>
            <FormGroup>
              {namespaces.map(({ label, namespace }) => (
                <Controller
                  key={namespace}
                  control={control}
                  name={`includedMetrics.${namespace}`}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value || false} />}
                      label={label}
                    />
                  )}
                />
              ))}
            </FormGroup>
          </Grid>
          <Grid item sx={{ display: 'flex', gap: '8px' }} xs={12}>
            <Button color="primary" type="submit" variant="contained">
              Generar
            </Button>
            <Button color="error" variant="contained" onClick={() => onCancel()}>
              Cancelar
            </Button>
          </Grid>
        </Grid>
      </form>
    </LocalizationProvider>
  )
}

export default AttestationReportForm
